import { initContract } from "@ts-rest/core";
import { z } from "zod";

import {
  ChatRequestType,
  CreateUserFileResponseType,
  DeleteChatMessagesType,
  LoadChatMessagesType,
  PersistChatMessagesType,
  RateChatMessageResponseType,
} from "@src/resources/chat/chat.service";
import { IMessage } from "@src/resources/chat/types";

const c = initContract();

export const ChatRequestSchema = z.object({
  messages: z.array(z.custom<IMessage>()),
  model: z.union([z.literal("openai"), z.literal("anthropic")]).optional(),
  stream: z.boolean().optional(),
  summaryId: z.string().optional(),
  technologyId: z.string().optional(),
});

const RateChatMessageSchema = z.object({
  issues: z.array(z.string()).nullable().optional(),
  message: z.string(),
  messageId: z.string(),
  promptType: z.string(),
  rating: z.union([z.literal("positive"), z.literal("negative")]),
  summaryId: z.string(),
  userFeedback: z.string().nullable().optional(),
});

const CreateUserFileSchema = z.object({
  fileName: z.string().min(1),
  model: z.union([z.literal("openai"), z.literal("anthropic")]).optional(),
  structure: z.string().min(1),
  userId: z.string().uuid(),
});

export type RateChatMessageType = z.infer<typeof RateChatMessageSchema>;
export type CreateUserFileType = z.infer<typeof CreateUserFileSchema>;

const index = c.router({
  chatRequest: {
    body: ChatRequestSchema,
    method: "POST",
    path: "/chat/",
    responses: {
      200: c.type<ChatRequestType>(),
    },
  },
  createUserFile: {
    body: CreateUserFileSchema,
    method: "POST",
    path: "/chat/create-user-files",
    responses: {
      200: c.type<CreateUserFileResponseType>(),
    },
  },
  deleteChatMessages: {
    body: z.object({
      technologyId: z.string(),
    }),
    method: "DELETE",
    path: "/chat/delete-chat-messages/",
    responses: {
      200: c.type<DeleteChatMessagesType>(),
    },
  },
  loadChatMessages: {
    method: "GET",
    path: "/chat/load-chat-messages/:technologyId",
    pathParams: z.object({
      technologyId: z.string(),
    }),
    responses: {
      200: c.type<LoadChatMessagesType>(),
    },
  },
  persistChatMessages: {
    body: z.object({
      messages: z.array(z.custom<IMessage>()),
      technologyId: z.string(),
    }),
    method: "POST",
    path: "/chat/chat-persist",
    responses: {
      200: c.type<PersistChatMessagesType>(),
    },
  },
  rateChatMessage: {
    body: RateChatMessageSchema,
    method: "POST",
    path: "/chat/rate-chat-message",
    responses: {
      200: c.type<RateChatMessageResponseType>(),
    },
  },
});

export default index;
